import { useState, type ReactNode } from 'react';
import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { GripVertical, Info, ListOrdered, Loader2, SlidersHorizontal } from 'lucide-react';

import { SPORT_LABELS, type Sport } from './sport';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { useTableState } from '@/hooks/useTableState';
import {
  type BookSnapshot,
  type ReviewLegStatus,
  type ReviewLeg,
  type SellOrder,
  type SidelineApi,
  type TapePoint
} from '@/api/sideline';

function cents(value: number | null | undefined): string {
  return typeof value === 'number' ? `${(value * 100).toFixed(1)}¢` : '—';
}

function money(value: number | null | undefined): string {
  if (typeof value !== 'number') {
    return '—';
  }
  return `${value < 0 ? '−' : '+'}$${Math.abs(value).toFixed(2)}`;
}

/** Wall clock to the millisecond, local time — the venues time-stamp to the millisecond and a race is decided in them. */
function clock(at: string): string {
  const d = new Date(at);
  return `${d.toLocaleTimeString([], { hour12: false })}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

/** Seconds between two instants, signed, to a tenth. */
function delta(from: string, to: string): string {
  const ms = Date.parse(to) - Date.parse(from);
  return `${ms < 0 ? '−' : '+'}${(Math.abs(ms) / 1000).toFixed(1)}s`;
}

/** A local-time value for `<input type="datetime-local">`. */
function localInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** What each column means, one sentence each, behind an (i) in the header. Rendered in the operator's saved order. */
interface Column {
  key: string;
  label: string;
  help: string;
  align?: 'right';
  className?: string;
  cell: (leg: ReviewLeg) => ReactNode;
}

const COLUMNS: Column[] = [
  {
    key: 'market',
    label: 'Market',
    help: 'The market and the outcome we bought. K marks Kalshi; the rest is Polymarket.',
    className: 'whitespace-nowrap',
    cell: leg => (
      <>
        <VenueTag venue={leg.venue} />
        {leg.market} <span className="text-muted-foreground">{leg.outcome}</span>
      </>
    )
  },
  {
    key: 'status',
    label: 'Status',
    help: 'What became of the leg. Filled: the whole size matched. Partial: fewer shares matched than were asked for. Missed: the order reached the venue and nothing matched — the asks under our limit were gone by the time it got there (on Polymarket, by the end of the one-second hold). Skipped: a gate refused the leg and no order was sent; the reason is underneath — no_edge (the ask was already within the edge kept of the model’s value), price_too_high / price_too_low (outside the prices we buy at), empty_book, spread_too_wide, stale_book, no_rung — or the tap never wanted the market at all: move_too_small (the goal moved the model’s value by under 10¢; the side shown is the one it leaned toward) or not_priced (no model or clock, or a first-half market after the break). Every market on the fixture is listed, on each venue that has it. Only for taps made since the service started recording every leg.',
    className: 'whitespace-nowrap',
    cell: leg => <StatusCell leg={leg} />
  },
  {
    key: 'seen',
    label: 'Seen at tap',
    help: 'What the service saw when it decided this leg: the bid and ask on its own copy of the book, and underneath the model’s value of the outcome after the goal. The limit is that value less the edge kept. Click it for the whole order book at the tap — every level, with shares and running dollars, and a line at our limit showing what the sweep could reach — next to the same book about a second later, which in play is the book a held order actually met. Polymarket’s is the live streamed book at that instant; Kalshi’s is its full book as last polled (every 2s), so up to two seconds old at the tap — the popup says how old — and read afresh for the second look. A dash means the tap predates the recording.',
    align: 'right',
    className: 'font-mono text-xs whitespace-nowrap',
    cell: leg => <SeenCell leg={leg} />
  },
  {
    key: 'boughtAt',
    label: 'Bought at',
    help: 'When our order matched, local time. Kalshi records it to the millisecond; Polymarket only to the second, after holding the order for one second.',
    className: 'font-mono text-xs',
    cell: leg =>
      leg.boughtAt ? (
        <span title={leg.sentAt ? `sent ${clock(leg.sentAt)}` : undefined}>
          {clock(leg.boughtAt)}
          {leg.venue === 'polymarket' ? <span className="text-muted-foreground"> ±1s</span> : null}
        </span>
      ) : (
        <span className="text-muted-foreground" title={leg.sentAt ? `sent ${clock(leg.sentAt)}` : undefined}>
          —
        </span>
      )
  },
  {
    key: 'tapToPost',
    label: 'Finger → venue',
    help: 'The whole trip of the order, one line per leg of it. Phone → server: half of the phone’s latest heartbeat round trip when it tapped — the tap travels one way, and no clock can time that leg directly, hence ≈; a dash for taps made before the page started sending it. Server: the tap arriving to the order leaving — planning, gates and picking the pre-signed order, all our own code. Server → venue: half of the round trip to the venue’s reply, hence ≈. The bold figure is the three added up: your finger to the order being at the venue. The venue’s own processing (and Polymarket’s in-play hold) comes after that.',
    align: 'right',
    className: 'font-mono text-xs whitespace-nowrap',
    cell: leg => {
      if (!leg.timing) {
        return <span className="text-muted-foreground">—</span>;
      }
      const { tapToPostMs, postToReplyMs, phoneRttMs } = leg.timing;
      // A service older than the field sends nothing at all, not null: anything but a number is no reading.
      const up = typeof phoneRttMs === 'number' && Number.isFinite(phoneRttMs) ? Math.round(phoneRttMs / 2) : null;
      const out = Math.round(postToReplyMs / 2);
      return (
        <>
          <strong>{up === null ? `${tapToPostMs + out}ms + phone` : `≈${up + tapToPostMs + out}ms`}</strong>
          <div className="text-muted-foreground">
            phone {up === null ? 'not recorded' : `≈${up}`} · server {tapToPostMs} · to venue ≈{out}
          </div>
        </>
      );
    }
  },
  {
    key: 'limit',
    label: 'Limit/Size',
    help: 'What the order asked for: the most it was allowed to pay per share — the model’s fair value after the goal, less the edge kept — and the shares or contracts requested. The order sweeps every ask from the best one up to the limit. A dash means nothing recorded the size: the order predates the service recording it and the venue has forgotten it.',
    align: 'right',
    className: 'font-mono whitespace-nowrap',
    cell: leg => (
      <>
        {cents(leg.limit)}
        <span className="text-muted-foreground">/</span>
        {leg.requested ?? '—'}
      </>
    )
  },
  {
    key: 'paid',
    label: 'Paid/Size',
    help: 'What actually filled: the average paid per share across the levels the sweep took, and the shares or contracts matched. On Polymarket the fill can exceed the size requested — a sweep pins dollars, not shares, so a limit far above the ask buys more. Paid well below the limit means the book was deep and cheap; close to it means the sweep ran up a thin book.',
    align: 'right',
    className: 'font-mono whitespace-nowrap',
    cell: leg => (
      <>
        {leg.paid === null ? <span className="text-muted-foreground">—</span> : cents(leg.paid)}
        <span className="text-muted-foreground">/</span>
        {leg.shares}
      </>
    )
  },
  {
    key: 'sellLimit',
    label: 'Sell limit/Size',
    help: 'The last resting sell the auto-sell had on this position: its limit — the model’s value of the outcome less 1¢ at the time — and its size, with how many sell orders were posted in all. Click it for every one of them in order: how long after the tap each went out, why (the tap, a score change the feed made, the clock a minute on, the spread rule, a retry), its price and size, the model’s value and the book when it was posted, and what it sold. A resting sell that sold nothing was cancelled by the next one. A dash means no sell was posted — the auto-sell was off, or this leg bought nothing.',
    align: 'right',
    className: 'font-mono whitespace-nowrap',
    cell: leg => <SellsCell leg={leg} />
  },
  {
    key: 'sold',
    label: 'Sold',
    help: 'What the auto-sell sold of this leg: the average fetched per share and how many, in the price of the outcome sold. Underneath, how it went: “rested” is the resting sell at the model’s value less 1¢ being taken by a buyer; “at bid” is the sale into the bid once the market’s spread had closed to 3¢. Kalshi holds one position per market, so sells close a market’s legs oldest first. A dash means nothing of it was sold — it was held, or the auto-sell was off.',
    align: 'right',
    className: 'font-mono whitespace-nowrap',
    cell: leg =>
      leg.exit ? (
        <>
          {cents(leg.exit.avgPrice)}
          <span className="text-muted-foreground">/</span>
          {leg.exit.sold}
          <div className="text-xs text-muted-foreground">
            {[
              leg.exit.maker > 0 ? `${leg.exit.maker} rested` : null,
              leg.exit.taker > 0 ? `${leg.exit.taker} at bid` : null
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </>
      ) : (
        <span className="text-muted-foreground">—</span>
      )
  },
  {
    key: 'before',
    label: 'Before',
    help: 'The last trade by anyone more than ten seconds before ours (before the tap, on a leg that bought nothing) — the market before the goal reached it. ≈ means nobody traded then and this is the venue’s minute quote.',
    cell: leg => <Point point={leg.before} />
  },
  {
    key: 'marketAfter',
    label: 'Market bought',
    help: 'The first trade by someone other than us from ten seconds before ours onward: its price and how long after ours it came (measured from the tap on a leg that bought nothing — there the price is what the market paid for what we did not get). A minus time means they were ahead of us. “no trade” means nobody else bought for half an hour; the ≈ figure is then the venue’s first minute quote after our fill, showing where it repriced.',
    cell: leg =>
      leg.marketAfter?.kind === 'quote' ? (
        <span className="font-mono text-xs">
          <span className="text-muted-foreground">no trade · </span>
          <Point point={leg.marketAfter} relativeTo={leg.at} />
        </span>
      ) : (
        <Point point={leg.marketAfter} relativeTo={leg.at} />
      )
  },
  {
    key: 'after',
    label: 'After 3 min',
    help: 'The last trade by anyone within three minutes of ours, or the venue’s quote three minutes on — where the market settled once it had priced the goal in. Compare with Paid — or, on a missed or skipped leg, with the ask seen at the tap: the gap is what the leg would have been worth.',
    cell: leg => <Point point={leg.after} />
  },
  {
    key: 'margin',
    label: 'Margin',
    help: 'The race. First line, from our own recording of the book: when the makers repriced or pulled the ask the tap hit, measured from the tap — green if our order matched before that, red if the makers moved first (on Polymarket the order is held one second, so they have that long). Second line, from the venue’s tape: the time from our fill to the next trade by anyone else within a minute, or that no one else bought in that minute — a trade minutes later belongs to the next goal. On a missed order whose limit was above the recorded ask, a recording that shows the ask holding is a recording that did not see the move — the row says so instead (the match of 18 Sep 2026 reads this way throughout: the book stream was dropping the makers’ cancels). Taps before the recorder existed have only the second line.',
    className: 'font-mono text-xs',
    cell: leg => (
      <>
        <MakersMoved leg={leg} />
        <TradeMargin leg={leg} />
      </>
    )
  },
  {
    key: 'result',
    label: 'Result',
    help: 'How the market resolved for the outcome we bought; open means it has not resolved yet.',
    cell: leg => <ResultBadge result={leg.result} />
  },
  {
    key: 'lead',
    label: 'Lead',
    help: 'What the tap was worth: (the price three minutes on − what we paid) × size, in dollars. The value of being early, whatever the match went on to do. Negative means we paid more than the market settled at once it had priced the goal in.',
    align: 'right',
    className: 'font-mono',
    cell: leg => (
      <span className={typeof leg.lead === 'number' && leg.lead < 0 ? 'text-red-500' : ''}>{money(leg.lead)}</span>
    )
  },
  {
    key: 'fees',
    label: 'Fees',
    help: 'What the venue charged on this leg, from its own fills: the fees on the buy, plus this leg’s share of the fees on the sells that closed it. Kalshi charges a taker about 1.75¢ a contract at 50¢ (less toward the extremes), so buying and selling 200 contracts at the bid is about $7 — set that against Lead and P&L, which are both before fees. A dash on Polymarket, which does not report a fee per fill.',
    align: 'right',
    className: 'font-mono',
    // A fee is a cost: shown as a minus, and a zero as a plain zero rather than "+$0.00".
    cell: leg =>
      typeof leg.fees !== 'number' ? (
        <span className="text-muted-foreground">—</span>
      ) : leg.fees === 0 ? (
        '$0.00'
      ) : (
        money(-leg.fees)
      )
  },
  {
    key: 'pnl',
    label: 'P&L',
    help: 'What the sold part fetched, plus the shares still held × (1 if won, 0 if lost), less what we paid. Before fees. A leg the auto-sell sold out has its P&L the moment it is sold, whatever the match goes on to do; a leg still partly held shows none until its market resolves.',
    align: 'right',
    className: 'font-mono',
    cell: leg => money(leg.pnl)
  }
];

/**
 * The columns in the operator's saved order, minus the ones hidden; any column the saved order
 * does not know goes at the end. Same state shape as the tables page: an order list and a
 * visibility map of column → false.
 */
function orderedColumns(order: string[], visibility: Record<string, boolean> = {}): Column[] {
  const known = order.filter(key => COLUMNS.some(c => c.key === key));
  const rest = COLUMNS.map(c => c.key).filter(key => !known.includes(key));
  return [...known, ...rest]
    .filter(key => visibility[key] !== false)
    .map(key => COLUMNS.find(c => c.key === key) as Column);
}

/** The same show/hide popover as the tables page: tick the columns to show, apply, remembered in the browser. */
function ColumnVisibility({
  visibility,
  onChange
}: {
  visibility: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const handleOpen = (next: boolean) => {
    if (next) {
      setDraft({ ...visibility });
    }
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <SlidersHorizontal className="mr-2 size-4" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[260px] p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <h4 className="text-sm font-semibold">Column Visibility</h4>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setDraft({})}
          >
            Reset
          </Button>
        </div>
        <div className="max-h-[360px] space-y-1 overflow-y-auto px-4 py-2">
          {COLUMNS.map(column => (
            <label key={column.key} className="flex items-center gap-2 rounded-sm px-1 py-1.5 text-sm hover:bg-accent">
              <Checkbox
                checked={draft[column.key] !== false}
                onCheckedChange={checked => setDraft(prev => ({ ...prev, [column.key]: checked === true }))}
              />
              {column.label}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onChange(draft);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The same drag-to-reorder popover as the tables page: drag rows into the order wanted, apply, and
 * it is saved for this page in the browser.
 */
function ColumnOrder({ order, onChange }: { order: string[]; onChange: (next: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [dragged, setDragged] = useState<string | null>(null);

  const handleOpen = (next: boolean) => {
    if (next) {
      setDraft(orderedColumns(order).map(c => c.key)); // every column, hidden ones too, so their place is kept
    }
    setOpen(next);
  };
  const dragOver = (e: React.DragEvent, target: string) => {
    e.preventDefault();
    if (!dragged || dragged === target) {
      return;
    }
    setDraft(prev => {
      const from = prev.indexOf(dragged);
      const to = prev.indexOf(target);
      if (from === -1 || to === -1) {
        return prev;
      }
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, dragged);
      return next;
    });
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <ListOrdered className="mr-2 size-4" />
          Column Order
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <h4 className="text-sm font-semibold">Reorder Columns</h4>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setDraft(COLUMNS.map(c => c.key))}
          >
            Reset
          </Button>
        </div>
        <div className="max-h-[360px] space-y-0.5 overflow-y-auto px-2 py-2">
          {draft.map(key => (
            <div
              key={key}
              draggable
              onDragStart={() => setDragged(key)}
              onDragOver={e => dragOver(e, key)}
              onDragEnd={() => setDragged(null)}
              className={`flex cursor-grab select-none items-center gap-2 rounded-md px-2 py-2 text-sm active:cursor-grabbing ${
                dragged === key ? 'bg-accent opacity-50' : 'hover:bg-accent'
              }`}
            >
              <GripVertical className="size-4 shrink-0 text-muted-foreground/50" />
              <span className="truncate">{COLUMNS.find(c => c.key === key)?.label ?? key}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onChange(draft);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ColumnHead({ label, help, align }: { label: string; help: string; align?: 'right' }) {
  return (
    <TableHead className={align === 'right' ? 'text-right' : undefined}>
      <span className="inline-flex items-center gap-1">
        {label}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`What ${label} means`}
              className="text-muted-foreground hover:text-foreground"
            >
              <Info className="size-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{help}</TooltipContent>
        </Tooltip>
      </span>
    </TableHead>
  );
}

/**
 * By how much we were first: the gap to the next trade by anyone else. Beaten reads red with how
 * many of their trades landed before ours; nobody else buying within the goal's minute reads as that.
 */
/** The makers' move off our own recording: when, measured from the tap, and whether our order matched first. */
function MakersMoved({ leg }: { leg: ReviewLeg }) {
  const r = leg.reaction;
  if (!r) {
    return null;
  }
  if (r.askAtTap === null) {
    return <div className="text-muted-foreground">no book at the tap</div>;
  }
  // A sweep up to a limit at or above the ask matches that ask if it is still there. So an order
  // that matched nothing against a recording that says the ask held is a recording that did not see
  // the makers move — not makers who held. Say what is known rather than what the recording shows.
  if (r.movedAt === null && leg.status === 'missed' && leg.limit !== null && leg.limit >= r.askAtTap - 1e-9) {
    return (
      <div
        className="text-red-500"
        title="Our order swept up to a limit above this ask and matched nothing, so the ask was gone when it matched. The recording did not see when."
      >
        {cents(r.askAtTap)} ask gone before our order matched
        <span className="text-muted-foreground"> · when was not recorded</span>
      </div>
    );
  }
  if (r.movedAt === null) {
    return (
      <div className="text-emerald-500">
        makers held {cents(r.askAtTap)} <span className="text-muted-foreground">10s+ after the tap</span>
      </div>
    );
  }
  const afterTap = (Date.parse(r.movedAt) - Date.parse(r.tappedAt)) / 1000;
  if (!leg.boughtAt) {
    // Nothing matched, so there is no fill to compare with: just when the makers moved.
    return (
      <div className={leg.status === 'missed' ? 'text-red-500' : 'text-muted-foreground'}>
        makers moved {cents(r.askAtTap)} → {r.askAfter === null ? 'gone' : cents(r.askAfter)} at +{afterTap.toFixed(2)}s
      </div>
    );
  }
  const beforeUs = Date.parse(r.movedAt) < Date.parse(leg.boughtAt);
  return (
    <div className={beforeUs ? 'text-red-500' : 'text-emerald-500'}>
      makers moved {cents(r.askAtTap)} → {r.askAfter === null ? 'gone' : cents(r.askAfter)} at +{afterTap.toFixed(2)}s
      <span className="text-muted-foreground"> {beforeUs ? 'before our order matched' : 'after we matched'}</span>
    </div>
  );
}

/** How long after a goal another buyer's trade still counts as the same race. */
const RACE_WINDOW_S = 60;

function TradeMargin({ leg }: { leg: ReviewLeg }) {
  const first = leg.beatenBy[0];
  if (!leg.boughtAt) {
    // A leg that bought nothing has no race of its own; say when others bought, against the tap.
    const next = first ?? (leg.marketAfter?.kind === 'trade' ? leg.marketAfter : null);
    const gap = next ? (Date.parse(next.at) - Date.parse(leg.at)) / 1000 : null;
    if (gap === null || gap > RACE_WINDOW_S) {
      return <span className="text-muted-foreground">no one bought within {RACE_WINDOW_S}s of the tap</span>;
    }
    return (
      <span className={gap < 0 ? 'text-red-500' : 'text-muted-foreground'}>
        others bought {gap < 0 ? `${Math.abs(gap).toFixed(1)}s before` : `${gap.toFixed(1)}s after`} the tap
        {leg.beatenBy.length > 1 ? (
          <span className="text-muted-foreground"> · {leg.beatenBy.length} trades</span>
        ) : null}
      </span>
    );
  }
  const bought = leg.boughtAt;
  if (first) {
    return (
      <span
        className="text-red-500"
        title={leg.beatenBy.map(p => `${cents(p.price)} ${delta(bought, p.at)}`).join(', ')}
      >
        beaten by {(Math.abs(Date.parse(first.at) - Date.parse(bought)) / 1000).toFixed(1)}s
        <span className="text-muted-foreground">
          {' '}
          · {leg.beatenBy.length} trade{leg.beatenBy.length === 1 ? '' : 's'} ahead
        </span>
      </span>
    );
  }
  // A trade minutes later belongs to the next goal, not this race: only the goal's own window counts.
  const gap =
    leg.marketAfter && leg.marketAfter.kind === 'trade'
      ? (Date.parse(leg.marketAfter.at) - Date.parse(bought)) / 1000
      : null;
  if (gap === null || gap > RACE_WINDOW_S) {
    return <span className="text-muted-foreground">no one else bought within {RACE_WINDOW_S}s</span>;
  }
  return <span className="text-emerald-500">first by {gap.toFixed(1)}s</span>;
}

const STATUS_STYLE: Record<ReviewLegStatus, string> = {
  filled: 'bg-emerald-600 hover:bg-emerald-600',
  partial: 'bg-amber-600 hover:bg-amber-600',
  missed: 'bg-red-600 hover:bg-red-600',
  skipped: 'bg-muted text-muted-foreground hover:bg-muted'
};

function StatusCell({ leg }: { leg: ReviewLeg }) {
  return (
    <>
      <Badge className={STATUS_STYLE[leg.status]}>{leg.status}</Badge>
      {leg.status === 'partial' && leg.requested ? (
        <div className="font-mono text-xs text-muted-foreground">
          {leg.shares} of {leg.requested}
        </div>
      ) : null}
      {leg.reason ? <div className="font-mono text-xs text-muted-foreground">{leg.reason}</div> : null}
    </>
  );
}

const STATUSES: ReviewLegStatus[] = ['filled', 'partial', 'missed', 'skipped'];

/** Running dollars from the best level outward, as the venue's own book shows them. */
function BookSide({
  levels,
  side,
  limit
}: {
  levels: BookSnapshot['asks'];
  side: 'ask' | 'bid';
  limit: number | null;
}) {
  let running = 0;
  const rows = levels.map(([price, size]) => {
    running += size === null ? 0 : price * size;
    return {
      price,
      size,
      total: size === null ? null : running,
      reach: side === 'ask' && limit !== null && price <= limit + 1e-9
    };
  });
  // Asks read like the venue's page: the best ask at the bottom, next to the spread.
  const ordered = side === 'ask' ? [...rows].reverse() : rows;
  if (!ordered.length) {
    return (
      <tr>
        <td colSpan={3} className="py-1 text-center text-muted-foreground">
          no {side}s
        </td>
      </tr>
    );
  }
  return (
    <>
      {ordered.map(row => (
        <tr key={`${side}-${row.price}`} className={row.reach ? 'bg-red-500/10' : ''}>
          <td className={`py-0.5 pr-3 text-right ${side === 'ask' ? 'text-red-500' : 'text-emerald-500'}`}>
            {cents(row.price)}
          </td>
          <td className="py-0.5 pr-3 text-right">{row.size === null ? '—' : row.size.toLocaleString()}</td>
          <td className="py-0.5 text-right text-muted-foreground">
            {row.total === null ? '—' : `$${row.total.toFixed(2)}`}
          </td>
        </tr>
      ))}
    </>
  );
}

function BookTable({
  title,
  book,
  limit,
  tappedAt
}: {
  title: string;
  book: BookSnapshot | null | undefined;
  limit: number | null;
  /** When given, a book read noticeably before the tap says how long before: Kalshi's is a poll, not a stream. */
  tappedAt?: number | null;
}) {
  if (!book) {
    return (
      <div className="min-w-44">
        <div className="mb-1 font-sans text-xs font-medium">{title}</div>
        <p className="font-sans text-xs text-muted-foreground">not recorded</p>
      </div>
    );
  }
  const bestBid = book.bids[0]?.[0] ?? null;
  const bestAsk = book.asks[0]?.[0] ?? null;
  return (
    <div className="min-w-52">
      <div className="mb-1 font-sans text-xs font-medium">
        {title} <span className="font-normal text-muted-foreground">{clock(new Date(book.at).toISOString())}</span>
      </div>
      <table className="w-full font-mono text-xs">
        <thead className="text-[10px] uppercase text-muted-foreground">
          <tr>
            <th className="pr-3 text-right font-normal">Price</th>
            <th className="pr-3 text-right font-normal">Shares</th>
            <th className="text-right font-normal">Total</th>
          </tr>
        </thead>
        <tbody>
          <BookSide levels={book.asks} side="ask" limit={limit} />
          <tr className="border-y border-border/60 text-muted-foreground">
            <td colSpan={3} className="py-1 text-center font-sans">
              spread {bestBid !== null && bestAsk !== null ? cents(bestAsk - bestBid) : '—'}
              {limit !== null ? <span> · our limit {cents(limit)}</span> : null}
            </td>
          </tr>
          <BookSide levels={book.bids} side="bid" limit={limit} />
        </tbody>
      </table>
      {book.depth ? null : (
        <p className="mt-1 font-sans text-[10px] text-muted-foreground">
          Top of book only, no sizes — the tap predates Kalshi&apos;s depth being polled, or no depth poll had answered.
        </p>
      )}
      {tappedAt && tappedAt - book.at > 250 ? (
        <p className="mt-1 font-sans text-[10px] text-muted-foreground">
          Polled {((tappedAt - book.at) / 1000).toFixed(1)}s before the tap — Kalshi&apos;s book is read every 2s, not
          streamed.
        </p>
      ) : null}
    </div>
  );
}

/** The bid/ask the service saw — and, on a click, the whole book it stood on, at the tap and a second later. */
function SeenCell({ leg }: { leg: ReviewLeg }) {
  const seen = leg.seen;
  if (!seen) {
    return <span className="text-muted-foreground">—</span>;
  }
  const tappedAt = leg.timing?.tappedAt ?? leg.reaction?.tappedAt ?? null;
  const summary = (
    <>
      {cents(seen.bid)}
      <span className="text-muted-foreground"> / </span>
      {cents(seen.ask)}
      <div className="text-muted-foreground">fair {cents(seen.fair)}</div>
    </>
  );
  if (!seen.book && !seen.bookAfter) {
    return <span title={seen.why ?? undefined}>{summary}</span>;
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded px-1 text-right underline decoration-dotted underline-offset-2 hover:bg-muted"
          title="Show the order book at the tap"
        >
          {summary}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[90vw]" align="end">
        <div className="mb-2 font-sans text-sm">
          <VenueTag venue={leg.venue} />
          {leg.market} <span className="text-muted-foreground">{leg.outcome}</span>
          {seen.why ? <div className="text-xs text-muted-foreground">{seen.why}</div> : null}
        </div>
        <div className="flex flex-wrap gap-6">
          <BookTable
            title="At the tap"
            book={seen.book}
            limit={leg.limit}
            tappedAt={tappedAt ? Date.parse(tappedAt) : null}
          />
          <BookTable title="About a second later" book={seen.bookAfter} limit={leg.limit} />
        </div>
        <p className="mt-2 max-w-md font-sans text-[11px] text-muted-foreground">
          Asks shaded red are at or under our limit — what the sweep could reach.{' '}
          {leg.venue === 'kalshi'
            ? 'Kalshi does not hold an order: ours matched within a tenth of a second of the tap, against the book on the left. The one on the right, read afresh a second later, is what was left and what the makers did next.'
            : 'In play Polymarket holds the order for a second, so it met the book on the right, not the one on the left.'}
        </p>
      </PopoverContent>
    </Popover>
  );
}

/** Seconds after the tap, to a tenth. */
function afterTap(ms: number): string {
  return `+${(ms / 1000).toFixed(1)}s`;
}

/** The last resting sell's limit and size — and, on a click, every sell the auto-sell posted for the position. */
function SellsCell({ leg }: { leg: ReviewLeg }) {
  const sells = leg.sells ?? [];
  const last = [...sells].reverse().find(s => s.kind === 'maker') ?? sells.at(-1);
  if (!last) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded px-1 text-right underline decoration-dotted underline-offset-2 hover:bg-muted"
          title="Show every sell order"
        >
          {cents(last.price)}
          <span className="text-muted-foreground">/</span>
          {last.size}
          <div className="text-xs text-muted-foreground">
            {sells.length} {sells.length === 1 ? 'order' : 'orders'}
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <VenueTag venue={leg.venue} />
            {leg.market} <span className="text-muted-foreground">{leg.outcome}</span> — sell orders
          </DialogTitle>
          <DialogDescription>
            Every sell the auto-sell posted for this position, in order. Each one replaced the one before it; what each
            sold is from the venue&apos;s fills.
          </DialogDescription>
        </DialogHeader>
        <SellOrdersTable sells={sells} />
      </DialogContent>
    </Dialog>
  );
}

function SellOrdersTable({ sells }: { sells: SellOrder[] }) {
  return (
    <table className="w-full font-mono text-xs">
      <thead className="text-[10px] uppercase text-muted-foreground">
        <tr>
          <th className="pr-3 text-left font-normal">After tap</th>
          <th className="pr-3 text-left font-normal">Posted</th>
          <th className="pr-3 text-left font-normal">Kind</th>
          <th className="pr-3 text-left font-normal">Why</th>
          <th className="pr-3 text-right font-normal">Price</th>
          <th className="pr-3 text-right font-normal">Size</th>
          <th className="pr-3 text-right font-normal">Fair</th>
          <th className="pr-3 text-right font-normal">Bid / Ask</th>
          <th className="text-right font-normal">Sold</th>
        </tr>
      </thead>
      <tbody>
        {sells.map(sell => (
          <tr key={sell.orderId} className="border-t border-border/60">
            <td className="py-1 pr-3">{afterTap(sell.afterMs)}</td>
            <td className="py-1 pr-3 text-muted-foreground">{clock(sell.postedAt)}</td>
            <td className="py-1 pr-3">{sell.kind === 'maker' ? 'resting' : 'at bid'}</td>
            <td className="py-1 pr-3 font-sans">{sell.reason}</td>
            <td className="py-1 pr-3 text-right">{cents(sell.price)}</td>
            <td className="py-1 pr-3 text-right">{sell.size}</td>
            <td className="py-1 pr-3 text-right text-muted-foreground">{cents(sell.fair)}</td>
            <td className="py-1 pr-3 text-right text-muted-foreground">
              {cents(sell.bid)} / {cents(sell.ask)}
            </td>
            <td className={`py-1 text-right ${sell.filled > 0 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
              {sell.filled > 0 ? `${sell.filled} @ ${cents(sell.avgPrice)}` : 'nothing'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VenueTag({ venue }: { venue: ReviewLeg['venue'] }) {
  return venue === 'kalshi' ? (
    <Badge variant="outline" className="mr-1 px-1 py-0 text-[10px] uppercase">
      K
    </Badge>
  ) : null;
}

function Point({ point, relativeTo }: { point: TapePoint | null; relativeTo?: string }) {
  if (!point) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span
      className="font-mono text-xs"
      title={point.kind === 'quote' ? 'no trade — the venue’s minute quote' : undefined}
    >
      {point.kind === 'quote' ? <span className="text-muted-foreground">≈</span> : null}
      {cents(point.price)}
      <span className="text-muted-foreground"> {relativeTo ? delta(relativeTo, point.at) : clock(point.at)}</span>
    </span>
  );
}

function ResultBadge({ result }: { result: ReviewLeg['result'] }) {
  if (result === 'won') {
    return <Badge className="bg-emerald-600 hover:bg-emerald-600">won</Badge>;
  }
  if (result === 'lost') {
    return <Badge variant="destructive">lost</Badge>;
  }
  return <Badge variant="outline">open</Badge>;
}

/** The post-match review for one sport: `api` is the service whose record it reads, `sport` the words and storage key. */
export function SidelineReviewPage({ api, sport }: { api: SidelineApi; sport: Sport }) {
  const labels = SPORT_LABELS[sport];
  const now = new Date();
  const [from, setFrom] = useState(localInput(new Date(now.getTime() - 24 * 60 * 60_000)));
  const [to, setTo] = useState(localInput(now));
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [shown, setShown] = useState<Record<ReviewLegStatus, boolean>>({
    filled: true,
    partial: true,
    missed: true,
    skipped: true
  });
  // The column order is remembered the way the tables page remembers its state, under the same key
  // scheme — one key per sport, so the two reviews keep their own columns.
  const tableState = useTableState({ tableName: `${labels.slug}-review` });
  const columns = orderedColumns(tableState.columnOrder, tableState.columnVisibility);

  const review = useQuery({
    queryKey: [`${labels.slug}-review`, range],
    queryFn: () => api.review(range!.from, range!.to),
    enabled: range !== null,
    staleTime: Infinity,
    retry: false
  });

  const load = () => {
    const f = new Date(from);
    const t = new Date(to);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime()) || t <= f) {
      return;
    }
    setRange({ from: f.toISOString(), to: t.toISOString() });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{labels.name} Review</h1>
        <span className="ml-auto text-xs text-muted-foreground">
          Read-only. Everything bought between two instants, on both venues, against each market&apos;s public tape.
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Window</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="datetime-local"
              value={from}
              onChange={e => setFrom(e.target.value)}
              className="sm:w-56"
              aria-label="From"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="datetime-local"
              value={to}
              onChange={e => setTo(e.target.value)}
              className="sm:w-56"
              aria-label="To"
            />
            <Button onClick={load} disabled={review.isFetching}>
              {review.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Load
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Pick the match, kick-off to the final whistle. Each tap is one table: for every leg, when we bought and what
            we paid, the last trade before ours (the market before the goal), the first trade after ours that was not
            ours (when the market caught up, and at what price), where the price stood three minutes on, and what the
            leg settled at. <strong>Beaten by</strong> lists anyone who traded in the ten seconds before us — the race,
            if there was one. A price marked ≈ is the venue&apos;s minute quote (Kalshi&apos;s ask, Polymarket&apos;s
            midpoint) where nobody traded at that moment. Times are to the millisecond because that is where a race is
            decided; Polymarket records a match only to the second, and holds every in-play order for one before
            matching it.
          </p>
          {review.error ? <p className="text-sm text-destructive">{String(review.error)}</p> : null}
        </CardContent>
      </Card>

      {review.data ? (
        <>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span>
              taps <strong>{review.data.taps.length}</strong>
            </span>
            <span>
              Polymarket <strong className="font-mono">{money(review.data.pnl.polymarket)}</strong>
            </span>
            <span>
              Kalshi <strong className="font-mono">{money(review.data.pnl.kalshi)}</strong>
            </span>
            <span className="ml-auto flex items-center gap-2">
              <ColumnVisibility visibility={tableState.columnVisibility} onChange={tableState.setColumnVisibility} />
              <ColumnOrder order={tableState.columnOrder} onChange={tableState.setColumnOrder} />
            </span>
          </div>
          {review.data.taps.length === 0 ? (
            <p className="text-sm text-muted-foreground">No taps in this window.</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Show</span>
            {STATUSES.map(status => {
              const count = review.data.taps.reduce((n, t) => n + t.legs.filter(l => l.status === status).length, 0);
              return (
                <Button
                  key={status}
                  size="sm"
                  variant={shown[status] ? 'default' : 'outline'}
                  onClick={() => setShown(current => ({ ...current, [status]: !current[status] }))}
                >
                  {status} <span className="ml-1 font-mono text-xs opacity-70">{count}</span>
                </Button>
              );
            })}
          </div>
          {review.data.taps.map(tap => (
            <Card key={tap.at}>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-baseline gap-3 text-base">
                  <span>Tap at {clock(tap.at)}</span>
                  <span className="text-sm font-normal text-muted-foreground">
                    {tap.legs.length} legs · {tap.legs.filter(l => l.boughtAt).length} bought ·{' '}
                    {tap.legs.filter(l => l.status === 'missed').length} missed ·{' '}
                    {tap.legs.filter(l => l.status === 'skipped').length} skipped
                  </span>
                  <span className="ml-auto font-mono text-sm">
                    <span className="text-muted-foreground">lead </span>
                    {money(tap.lead)}
                    <span className="text-muted-foreground"> · P&amp;L </span>
                    {money(tap.pnl)}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map(column => (
                        <ColumnHead key={column.key} label={column.label} help={column.help} align={column.align} />
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tap.legs
                      .filter(leg => shown[leg.status])
                      .map(leg => (
                        <TableRow
                          key={`${leg.venue}-${leg.market}-${leg.outcome}-${leg.orderId ?? leg.status}`}
                          className={leg.boughtAt ? '' : 'opacity-70'}
                        >
                          {columns.map(column => (
                            <TableCell
                              key={column.key}
                              className={[column.align === 'right' ? 'text-right' : '', column.className ?? '']
                                .filter(Boolean)
                                .join(' ')}
                            >
                              {column.cell(leg)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </>
      ) : null}
    </div>
  );
}
